import { FunctionComponent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faCheck, faClock, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Moment } from 'moment-timezone';
import ClickAwayListener from 'react-click-away-listener';

import {
    approveVaccineApprovalThunk,
    convertToVaccineApprovalsPage,
    declineVaccineApprovalThunk,
    fetchVaccineApprovalThunk
} from '@spike/vaccine-approvals-action';
import {
    VaccineApproval,
    VaccineApprovalDetail,
    VaccineApprovalDetailMode,
    VaccineApprovalStatus,
    VaccineApprovalVaccineItem
} from 'model/VaccineApproval';

import {
    FullWindowModal,
    FullWindowModalBody,
    FullWindowModalCloseButton,
    FullWindowModalHeader
} from 'components/UI/V3/Modals/FullWindowModal';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/Spinner';
import PetInfo from 'components/OrderDetail/PetDetail/PetInfo';
import VaccineApprovalDatePicker from './VaccineApprovalDatePicker';

import { useApiClientWrapper, useMasterData, useTimeZone } from 'hooks';
import { RootState } from 'store';
import { formatExpirationDate, getClientDisplayName, getVaccineFileUrl, mapVaccineApprovalPetToPet } from '../utils';

import styles from './VaccineApprovalDetailModal.module.scss';

interface VaccineApprovalDetailModalProps {
    item: VaccineApproval;
    detail: VaccineApprovalDetail | null;
    loading: boolean;
    mode: VaccineApprovalDetailMode;
    onClose: () => void;
    onReviewed?: () => void;
}

const STATUS_LABELS: Record<VaccineApprovalStatus, string> = {
    pending: 'Pending',
    approved: 'Approved',
    declined: 'Declined'
};

const formatReceivedOn = (date: Moment | undefined, timeZone: string): string => {
    if (!date) return '—';
    return date.clone().tz(timeZone).format('dddd, D MMM YYYY - hh:mmA');
};

const getDocumentUrl = (detail: VaccineApprovalDetail): string | undefined => {
    return getVaccineFileUrl(detail);
};

const getDocumentTitle = (vaccineName?: string): string => {
    const name = vaccineName?.trim();
    return name ? `${name} Vaccination Record` : 'Vaccination Record';
};

const isImageUrl = (url: string): boolean => /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);

const VaccineStatusBadge: FunctionComponent<{ status: VaccineApprovalStatus; selected?: boolean }> = ({
    status,
    selected = false
}) => (
    <span
        className={styles.statusBadge}
        data-status={status}
        data-selected={selected ? 'true' : undefined}
    >
        {status === 'pending' && <FontAwesomeIcon icon={faClock} />}
        {status === 'approved' && <FontAwesomeIcon icon={faCheck} />}
        {status === 'declined' && <FontAwesomeIcon icon={faXmark} />}
        {STATUS_LABELS[status]}
    </span>
);

const VaccineApprovalDetailModal: FunctionComponent<VaccineApprovalDetailModalProps> = ({
    item,
    detail,
    loading,
    onClose,
    onReviewed
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const masterData = useMasterData();
    const timeZone = useTimeZone();
    const requests = useSelector<RootState, VaccineApproval[]>(state => state.vaccineApprovals.requests ?? []);

    const [actionLoading, setActionLoading] = useState<'approve' | 'decline' | null>(null);
    const [selectedVaccineId, setSelectedVaccineId] = useState(item.vaccineId);
    const [activeDetail, setActiveDetail] = useState<VaccineApprovalDetail>(detail || item);
    const [previewLoading, setPreviewLoading] = useState(loading);
    const [expirationOverrides, setExpirationOverrides] = useState<Record<string, Moment>>({});
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [datePickerPosition, setDatePickerPosition] = useState({ top: 0, left: 0 });
    const [siblingApprovals, setSiblingApprovals] = useState<VaccineApproval[]>([]);
    const expirationTriggerRef = useRef<HTMLButtonElement>(null);
    const datePickerPopoverRef = useRef<HTMLDivElement>(null);
    const siblingLookupKeyRef = useRef<string | null>(null);
    const apiClientWrapperRef = useRef(apiClientWrapper);
    apiClientWrapperRef.current = apiClientWrapper;

    const updateDatePickerPosition = useCallback(() => {
        const trigger = expirationTriggerRef.current;
        if (!trigger) {
            return;
        }

        const rect = trigger.getBoundingClientRect();
        const calendarWidth = 320;
        const gap = 8;
        const estimatedHeight = 340;
        const popoverHeight = datePickerPopoverRef.current?.offsetHeight || estimatedHeight;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const openAbove = spaceBelow < popoverHeight;

        let top = openAbove ? rect.top - popoverHeight - gap : rect.bottom + gap;
        top = Math.max(8, Math.min(top, window.innerHeight - popoverHeight - 8));

        const left = Math.min(
            Math.max(8, rect.left + rect.width / 2 - calendarWidth / 2),
            window.innerWidth - calendarWidth - 8
        );

        setDatePickerPosition({ top, left });
    }, []);

    useEffect(() => {
        setSelectedVaccineId(item.vaccineId);
        setActiveDetail(detail || item);
        setExpirationOverrides({});
        setDatePickerOpen(false);
        setSiblingApprovals([]);
        siblingLookupKeyRef.current = null;
    }, [item, detail]);

    useEffect(() => {
        setPreviewLoading(loading);
    }, [loading]);

    // Load all sibling approvals for this submission (across statuses) so vaccine
    // selection works even when the list tab only has approved/declined rows.
    useEffect(() => {
        const source = detail || item;
        const lookupKey = source.submissionUuid || `${source.petId}-${source.customerId}`;
        if (siblingLookupKeyRef.current === lookupKey) {
            return;
        }

        let cancelled = false;
        siblingLookupKeyRef.current = lookupKey;

        const loadSiblings = async () => {
            const wrapper = apiClientWrapperRef.current;
            const petName = source.pet?.name?.trim();
            try {
                const response = await wrapper.apiClient.get('/vaccine_approvals', {
                    ...wrapper.requestConfig,
                    params: {
                        ...wrapper.requestConfig.params,
                        marketplace_id: wrapper.marketplaceId,
                        status: 'all',
                        search: petName || undefined,
                        page: 1,
                        size: 100
                    }
                });
                if (cancelled) {
                    return;
                }

                const page = convertToVaccineApprovalsPage(response.data);
                const siblings = page.requests.filter(request => {
                    if (source.submissionUuid) {
                        return request.submissionUuid === source.submissionUuid;
                    }
                    return request.petId === source.petId && request.customerId === source.customerId;
                });

                setSiblingApprovals(siblings.length ? siblings : [source]);
            } catch {
                if (!cancelled) {
                    const fallback = requests.filter(request => {
                        if (source.submissionUuid) {
                            return request.submissionUuid === source.submissionUuid;
                        }
                        return request.petId === source.petId && request.customerId === source.customerId;
                    });
                    setSiblingApprovals(fallback.length ? fallback : [source]);
                }
            }
        };

        void loadSiblings();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, detail?.id, detail?.submissionUuid, item.submissionUuid, item.petId, item.customerId]);

    useEffect(() => {
        if (!datePickerOpen) {
            return;
        }

        updateDatePickerPosition();
        // Recalculate after layout so we can use the real calendar height.
        const frameId = window.requestAnimationFrame(() => updateDatePickerPosition());

        const handleScrollOrResize = () => {
            if (!expirationTriggerRef.current) {
                setDatePickerOpen(false);
                return;
            }

            const rect = expirationTriggerRef.current.getBoundingClientRect();
            const stillVisible =
                rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;

            if (!stillVisible) {
                setDatePickerOpen(false);
                return;
            }

            updateDatePickerPosition();
        };

        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [datePickerOpen, updateDatePickerPosition]);

    const vaccines: VaccineApprovalVaccineItem[] = useMemo(() => {
        const list = activeDetail.vaccines?.length ? activeDetail.vaccines : detail?.vaccines || item.vaccines || [];
        const withStatus = (
            list.length
                ? list
                : [
                      {
                          id: activeDetail.vaccineId || item.vaccineId,
                          name: activeDetail.vaccineId || item.vaccineId,
                          expirationDate: activeDetail.expirationDate || item.expirationDate,
                          status: activeDetail.status || item.status
                      }
                  ]
        ).map(vaccine => {
            if (vaccine.status) {
                return vaccine;
            }
            const sibling = siblingApprovals.find(request => String(request.vaccineId) === String(vaccine.id));
            return sibling ? { ...vaccine, status: sibling.status } : vaccine;
        });

        return withStatus;
    }, [activeDetail, detail, item, siblingApprovals]);

    useEffect(() => {
        if (!vaccines.length) {
            return;
        }

        const hasSelected = vaccines.some(vaccine => String(vaccine.id) === String(selectedVaccineId));
        if (hasSelected) {
            return;
        }

        const match = vaccines.find(vaccine => String(vaccine.id) === String(activeDetail.vaccineId));
        setSelectedVaccineId((match || vaccines[0]).id);
    }, [vaccines, selectedVaccineId, activeDetail.vaccineId]);

    const pet = useMemo(() => mapVaccineApprovalPetToPet(activeDetail.pet, masterData), [activeDetail.pet, masterData]);
    const clientName = getClientDisplayName(activeDetail.client.firstName, activeDetail.client.lastName);
    const clientPhone = activeDetail.client.phone?.trim();
    const clientEmail = activeDetail.client.email?.trim();
    const clientAddress = activeDetail.client.address?.trim();
    const documentUrl = getDocumentUrl(activeDetail);
    const selectedVaccineName = useMemo(() => {
        const selectedVaccine = vaccines.find(vaccine => String(vaccine.id) === String(selectedVaccineId));
        return selectedVaccine?.name || activeDetail.vaccines?.[0]?.name || activeDetail.vaccineId;
    }, [vaccines, selectedVaccineId, activeDetail.vaccines, activeDetail.vaccineId]);
    const documentTitle = getDocumentTitle(selectedVaccineName);

    const getVaccineExpirationDate = (vaccine: VaccineApprovalVaccineItem): Moment | undefined => {
        return (
            expirationOverrides[String(vaccine.id)] ||
            vaccine.expirationDate ||
            (String(vaccine.id) === String(activeDetail.vaccineId) ? activeDetail.expirationDate : undefined)
        );
    };

    const selectedExpirationDate = useMemo(() => {
        const selectedVaccine = vaccines.find(vaccine => String(vaccine.id) === String(selectedVaccineId));
        if (!selectedVaccine) {
            return activeDetail.expirationDate;
        }
        return getVaccineExpirationDate(selectedVaccine);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vaccines, selectedVaccineId, expirationOverrides, activeDetail.expirationDate, activeDetail.vaccineId]);

    const selectedVaccineStatus = useMemo(() => {
        const selectedVaccine = vaccines.find(vaccine => String(vaccine.id) === String(selectedVaccineId));
        return selectedVaccine?.status || activeDetail.status;
    }, [vaccines, selectedVaccineId, activeDetail.status]);

    const showReviewActions = selectedVaccineStatus === 'pending';

    const resolveApprovalIdForVaccine = useCallback(
        (vaccineId: string): number | undefined => {
            if (String(activeDetail.vaccineId) === String(vaccineId)) {
                return activeDetail.id;
            }
            if (String(item.vaccineId) === String(vaccineId)) {
                return item.id;
            }

            const fromSiblings = siblingApprovals.find(request => String(request.vaccineId) === String(vaccineId));
            if (fromSiblings) {
                return fromSiblings.id;
            }

            const sameSubmission = activeDetail.submissionUuid
                ? requests.find(
                      request =>
                          request.submissionUuid === activeDetail.submissionUuid &&
                          String(request.vaccineId) === String(vaccineId)
                  )
                : undefined;
            if (sameSubmission) {
                return sameSubmission.id;
            }

            return requests.find(
                request =>
                    request.petId === activeDetail.petId &&
                    request.customerId === activeDetail.customerId &&
                    String(request.vaccineId) === String(vaccineId)
            )?.id;
        },
        [activeDetail, item, siblingApprovals, requests]
    );

    const fetchDetailForVaccine = useCallback(
        async (vaccineId: string) => {
            const approvalId = resolveApprovalIdForVaccine(vaccineId);
            if (!approvalId) {
                return;
            }

            if (approvalId === activeDetail.id && String(activeDetail.vaccineId) === String(vaccineId)) {
                return;
            }

            setPreviewLoading(true);
            try {
                const fetched = await (dispatch as any)(fetchVaccineApprovalThunk(apiClientWrapper as any, approvalId));
                if (fetched) {
                    setActiveDetail(fetched);
                }
            } catch {
                // reducer handles error
            } finally {
                setPreviewLoading(false);
            }
        },
        [resolveApprovalIdForVaccine, activeDetail.id, activeDetail.vaccineId, dispatch, apiClientWrapper]
    );

    // If the user selected another vaccine before siblings finished loading, fetch once ready.
    useEffect(() => {
        if (!siblingApprovals.length) {
            return;
        }
        if (String(selectedVaccineId) === String(activeDetail.vaccineId)) {
            return;
        }
        void fetchDetailForVaccine(selectedVaccineId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siblingApprovals]);

    const handleSelectVaccine = async (vaccine: VaccineApprovalVaccineItem) => {
        if (String(selectedVaccineId) === String(vaccine.id) && !previewLoading) {
            return;
        }

        setSelectedVaccineId(vaccine.id);
        setDatePickerOpen(false);
        setExpirationOverrides({});
        await fetchDetailForVaccine(vaccine.id);
    };

    const handleExpirationChange = (date: Moment | null) => {
        if (!date) {
            return;
        }

        setExpirationOverrides(prev => ({
            ...prev,
            [String(selectedVaccineId)]: date
        }));
        setDatePickerOpen(false);
    };

    const openExpirationDatePicker = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (datePickerOpen) {
            setDatePickerOpen(false);
            return;
        }

        updateDatePickerPosition();
        // Defer open so the opening click does not immediately trigger click-away.
        window.setTimeout(() => {
            updateDatePickerPosition();
            setDatePickerOpen(true);
        }, 0);
    };

    const handleApprove = async () => {
        setActionLoading('approve');
        try {
            await (dispatch as any)(
                approveVaccineApprovalThunk(apiClientWrapper as any, activeDetail.id, selectedExpirationDate)
            );
            onReviewed?.();
            onClose();
        } catch {
            // reducer handles error
        } finally {
            setActionLoading(null);
        }
    };

    const handleDecline = async () => {
        setActionLoading('decline');
        try {
            await (dispatch as any)(
                declineVaccineApprovalThunk(apiClientWrapper as any, activeDetail.id, selectedExpirationDate)
            );
            onReviewed?.();
            onClose();
        } catch {
            // reducer handles error
        } finally {
            setActionLoading(null);
        }
    };

    return (
        <FullWindowModal>
            <FullWindowModalHeader>
                <div className={styles.headerLayout}>
                    <img
                        className={styles.logo}
                        src="/images/logo/cuddles-logo.svg"
                        alt="Cuddles"
                    />
                    <h2 className={styles.headerTitle}>Vaccine To Approve</h2>
                    <FullWindowModalCloseButton
                        onClick={onClose}
                        ariaLabel="Close vaccine approval"
                        className={styles.closeButton}
                    />
                </div>
            </FullWindowModalHeader>

            <FullWindowModalBody className={styles.body}>
                <aside className={styles.detailsPane}>
                    <div className={styles.receivedOn}>
                        <span>Received on</span>
                        <span className={styles.receivedDate}>
                            {formatReceivedOn(activeDetail.receivedAt, timeZone)}
                        </span>
                    </div>

                    <div className={styles.clientBlock}>
                        <p className={styles.clientName}>{clientName || '—'}</p>
                        {clientPhone && <p className={styles.clientMeta}>{clientPhone}</p>}
                        {clientEmail && <p className={styles.clientMeta}>{clientEmail}</p>}
                        {clientAddress && <p className={styles.clientMeta}>{clientAddress}</p>}
                    </div>

                    <PetInfo
                        pet={pet}
                        className={styles.petInfo}
                    />

                    <div className={styles.vaccinesTable}>
                        <h3 className={styles.vaccinesSectionTitle}>Vaccines</h3>
                        <div className={styles.vaccinesHeader}>
                            <span>Vaccine</span>
                            <span>Expiration</span>
                            <span>Status</span>
                        </div>
                        <div className={styles.vaccinesList}>
                            {vaccines.map(vaccine => {
                                const isSelected = String(selectedVaccineId) === String(vaccine.id);
                                const expirationDate = getVaccineExpirationDate(vaccine);
                                const vaccineStatus = vaccine.status || activeDetail.status;
                                const canEditExpiration =
                                    isSelected && showReviewActions && vaccineStatus === 'pending';

                                const expirationContent = (
                                    <>
                                        <FontAwesomeIcon icon={faCalendarDays} />
                                        {formatExpirationDate(expirationDate, timeZone) || '—'}
                                    </>
                                );

                                return (
                                    <div
                                        key={vaccine.id}
                                        role="button"
                                        tabIndex={0}
                                        className={clsx(styles.vaccinesRow, isSelected && styles.selected)}
                                        onClick={() => handleSelectVaccine(vaccine)}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleSelectVaccine(vaccine);
                                            }
                                        }}
                                        aria-pressed={isSelected}
                                    >
                                        <span className={styles.vaccineName}>{vaccine.name}</span>
                                        {canEditExpiration ? (
                                            <button
                                                ref={expirationTriggerRef}
                                                type="button"
                                                className={clsx(styles.expirationField, styles.expirationFieldEditable)}
                                                onClick={openExpirationDatePicker}
                                                aria-expanded={datePickerOpen}
                                                aria-haspopup="dialog"
                                            >
                                                {expirationContent}
                                            </button>
                                        ) : (
                                            <span className={styles.expirationField}>{expirationContent}</span>
                                        )}
                                        <VaccineStatusBadge
                                            status={vaccineStatus}
                                            selected={isSelected}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {datePickerOpen &&
                        createPortal(
                            <ClickAwayListener onClickAway={() => setDatePickerOpen(false)}>
                                <div
                                    ref={datePickerPopoverRef}
                                    className={styles.datePickerPopover}
                                    style={{
                                        top: datePickerPosition.top,
                                        left: datePickerPosition.left
                                    }}
                                    role="dialog"
                                    aria-modal="true"
                                    aria-label="Select expiration date"
                                >
                                    <VaccineApprovalDatePicker
                                        timeZone={timeZone}
                                        value={selectedExpirationDate || null}
                                        onChange={handleExpirationChange}
                                    />
                                </div>
                            </ClickAwayListener>,
                            document.body
                        )}
                </aside>

                <section className={styles.previewPane}>
                    <div className={styles.documentFrame}>
                        <h3 className={styles.documentTitle}>{documentTitle}</h3>
                        {previewLoading ? (
                            <div className={styles.documentPlaceholder}>
                                <Spinner />
                            </div>
                        ) : documentUrl ? (
                            isImageUrl(documentUrl) ? (
                                <img
                                    className={styles.documentImage}
                                    src={documentUrl}
                                    alt={documentTitle}
                                />
                            ) : (
                                <iframe
                                    className={styles.documentIframe}
                                    title={documentTitle}
                                    src={documentUrl}
                                />
                            )
                        ) : (
                            <div className={styles.documentPlaceholder}>No vaccine file available.</div>
                        )}
                    </div>
                </section>

                {showReviewActions && (
                    <div className={styles.footerActions}>
                        <Button
                            label="Decline"
                            variant="danger"
                            size="large"
                            onClick={handleDecline}
                            isLoading={actionLoading === 'decline'}
                            disabled={actionLoading !== null || previewLoading}
                            className={clsx(styles.actionButton, styles.declineButton)}
                        />
                        <Button
                            label="Approve"
                            variant="green"
                            size="large"
                            onClick={handleApprove}
                            isLoading={actionLoading === 'approve'}
                            disabled={actionLoading !== null || previewLoading}
                            className={clsx(styles.actionButton, styles.approveButton)}
                        />
                    </div>
                )}
            </FullWindowModalBody>
        </FullWindowModal>
    );
};

export default VaccineApprovalDetailModal;
